import { ChevronDown } from 'lucide-react';
import {
  type ReactNode,
  type RefObject,
  forwardRef,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

/**
 * Sidebar chrome: collapsible sections, the floating menu portal,
 * section action menus, and small row/route buttons. Extracted from
 * Sidebar.tsx.
 */

export function SidebarSection({
  title,
  action,
  children,
}: {
  title: string;
  action?: JSX.Element;
  children: JSX.Element | JSX.Element[];
}): JSX.Element {
  const [collapsed, setCollapsed] = useState(false);
  const sectionId = `sidebar-section-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;

  return (
    <section>
      <div className="group/section grid min-h-8 grid-cols-[minmax(0,1fr)_auto] items-center gap-1">
        <button
          type="button"
          aria-expanded={!collapsed}
          aria-controls={sectionId}
          onClick={() => setCollapsed((value) => !value)}
          className="flex min-w-0 items-center gap-1.5 py-1.5 pr-2 text-left text-[13px] font-medium leading-5 text-[color:var(--app-text)] transition hover:text-[color:var(--app-accent-text)]"
        >
          <ChevronDown
            aria-hidden="true"
            size={13}
            strokeWidth={2.2}
            className={`shrink-0 transition-transform ${collapsed ? '-rotate-90' : ''}`}
          />
          <span className="truncate">{title}</span>
        </button>
        {action && (
          <div
            onClick={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
            className="justify-self-end opacity-0 transition-opacity group-hover/section:opacity-100 [&:has(:focus-visible)]:opacity-100 [&:has([aria-expanded=true])]:opacity-100"
          >
            {action}
          </div>
        )}
      </div>
      <div
        id={sectionId}
        aria-hidden={collapsed || undefined}
        className={`grid overflow-hidden transition-[grid-template-rows,opacity] duration-150 ease-out ${
          collapsed ? 'grid-rows-[0fr] opacity-0' : 'grid-rows-[1fr] opacity-100'
        }`}
      >
        <div className="min-h-0 overflow-hidden pb-2">{children}</div>
      </div>
    </section>
  );
}

export function SidebarFloatingMenu({
  triggerRef,
  widthClassName,
  children,
}: {
  triggerRef: RefObject<HTMLElement>;
  widthClassName: string;
  children: ReactNode;
}): JSX.Element {
  const menuRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ left: number; top: number } | undefined>();

  useLayoutEffect(() => {
    const updatePosition = (): void => {
      const trigger = triggerRef.current;
      const menu = menuRef.current;
      if (!trigger || !menu) return;

      const triggerRect = trigger.getBoundingClientRect();
      const menuRect = menu.getBoundingClientRect();
      const sidebarRect = trigger.closest('.workspace-sidebar')?.getBoundingClientRect();
      const viewportGutter = 8;
      const menuWidth = menuRect.width;
      const menuHeight = menuRect.height;
      const sidebarLeft = sidebarRect?.left ?? viewportGutter;
      const sidebarRight = sidebarRect?.right ?? window.innerWidth - viewportGutter;

      let left = Math.min(triggerRect.right, sidebarRight - viewportGutter) - menuWidth;
      left = Math.max(sidebarLeft + viewportGutter, left);
      left = Math.min(window.innerWidth - menuWidth - viewportGutter, left);

      let top = triggerRect.bottom + 2;
      if (top + menuHeight > window.innerHeight - viewportGutter) {
        top = Math.max(viewportGutter, triggerRect.top - menuHeight - 2);
      }

      setPosition({ left: Math.round(left), top: Math.round(top) });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [triggerRef]);

  return createPortal(
    <div
      ref={menuRef}
      role="menu"
      style={{
        left: position ? `${position.left}px` : '-9999px',
        top: position ? `${position.top}px` : '-9999px',
        visibility: position ? 'visible' : 'hidden',
      }}
      className={`fixed z-30 rounded-md border border-[color:var(--app-border)] bg-[color:var(--app-panel-strong)] py-1 text-xs shadow-lg ${widthClassName}`}
    >
      {children}
    </div>,
    document.body,
  );
}

export function SavedViewsSectionMenu({ onCreate }: { onCreate(): void }): JSX.Element {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  return (
    <div className="relative justify-self-end">
      <SectionMenuButton
        ref={triggerRef}
        label="Saved views section actions"
        testId="saved-views-section-menu-button"
        open={open}
        onToggle={() => setOpen((value) => !value)}
        onClose={() => setOpen(false)}
      />
      {open && (
        <SidebarFloatingMenu triggerRef={triggerRef} widthClassName="w-40">
          <button
            type="button"
            role="menuitem"
            data-testid="new-smart-list-button"
            onMouseDown={(event) => event.preventDefault()}
            onClick={(event) => {
              event.stopPropagation();
              setOpen(false);
              onCreate();
            }}
            className="block w-full px-2 py-1.5 text-left font-medium text-[color:var(--app-text)] hover:bg-[rgba(29,37,36,0.08)]"
          >
            New saved view
          </button>
        </SidebarFloatingMenu>
      )}
    </div>
  );
}

export function ProjectSectionMenu({
  sort,
  countMode,
  showArchived,
  onCreate,
  onChangeSort,
  onChangeCountMode,
  onChangeShowArchived,
}: {
  sort: 'alphabetical' | 'manual';
  countMode: 'active' | 'total';
  showArchived: boolean;
  onCreate(): void;
  onChangeSort(sort: 'alphabetical' | 'manual'): void;
  onChangeCountMode(countMode: 'active' | 'total'): void;
  onChangeShowArchived(showArchived: boolean): void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  return (
    <div className="relative justify-self-end">
      <SectionMenuButton
        ref={triggerRef}
        label="Project section actions"
        testId="project-section-menu-button"
        open={open}
        onToggle={() => setOpen((value) => !value)}
        onClose={() => setOpen(false)}
      />
      {open && (
        <SidebarFloatingMenu triggerRef={triggerRef} widthClassName="w-52">
          <button
            type="button"
            role="menuitem"
            data-testid="new-project-button"
            onMouseDown={(event) => event.preventDefault()}
            onClick={(event) => {
              event.stopPropagation();
              setOpen(false);
              onCreate();
            }}
            className="block w-full px-2 py-1.5 text-left font-medium text-[color:var(--app-text)] hover:bg-[rgba(29,37,36,0.08)]"
          >
            New project
          </button>
          <MenuChoiceGroup label="Order">
            <MenuChoice
              label="Alphabetical"
              selected={sort === 'alphabetical'}
              onSelect={() => onChangeSort('alphabetical')}
            />
            <MenuChoice
              label="Drag and drop"
              selected={sort === 'manual'}
              onSelect={() => onChangeSort('manual')}
            />
          </MenuChoiceGroup>
          <MenuChoiceGroup label="Count">
            <MenuChoice
              label="Active tasks"
              selected={countMode === 'active'}
              onSelect={() => onChangeCountMode('active')}
            />
            <MenuChoice
              label="All tasks"
              selected={countMode === 'total'}
              onSelect={() => onChangeCountMode('total')}
            />
          </MenuChoiceGroup>
          <label className="flex cursor-pointer items-center gap-2 px-2 py-1.5 text-[color:var(--app-text)] hover:bg-[rgba(29,37,36,0.08)]">
            <input
              type="checkbox"
              checked={showArchived}
              onMouseDown={(event) => event.preventDefault()}
              onChange={(event) => onChangeShowArchived(event.target.checked)}
            />
            Show archived projects
          </label>
        </SidebarFloatingMenu>
      )}
    </div>
  );
}

export const SectionMenuButton = forwardRef<
  HTMLButtonElement,
  {
    label: string;
    testId: string;
    open: boolean;
    onToggle(): void;
    onClose(): void;
  }
>(function SectionMenuButton({ label, testId, open, onToggle, onClose }, ref): JSX.Element {
  return (
    <button
      ref={ref}
      type="button"
      aria-label={label}
      aria-haspopup="menu"
      aria-expanded={open || undefined}
      data-testid={testId}
      onClick={onToggle}
      onBlur={() => window.setTimeout(onClose, 120)}
      className="flex h-6 w-6 items-center justify-center rounded-sm text-[color:var(--app-text-muted)] transition hover:bg-white/65 hover:text-[color:var(--app-text)]"
    >
      <span
        className="h-3 w-3 bg-current"
        style={{
          clipPath: 'path("M 0 0 v 4 h 9 l -3 3 l -3 -3 h -3 v 8 h 12 v -12 h -12 Z")',
        }}
        aria-hidden="true"
      />
    </button>
  );
});

function MenuChoiceGroup({
  label,
  children,
}: {
  label: string;
  children: JSX.Element | JSX.Element[];
}): JSX.Element {
  return (
    <div className="border-t border-[color:var(--app-border)] py-1">
      <p className="px-2 pb-1 pt-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-[color:var(--app-text-soft)]">
        {label}
      </p>
      {children}
    </div>
  );
}

function MenuChoice({
  label,
  selected,
  onSelect,
}: {
  label: string;
  selected: boolean;
  onSelect(): void;
}): JSX.Element {
  return (
    <button
      type="button"
      role="menuitemradio"
      aria-checked={selected}
      onMouseDown={(event) => event.preventDefault()}
      onClick={(event) => {
        event.stopPropagation();
        onSelect();
      }}
      className="grid w-full grid-cols-[1rem_minmax(0,1fr)] items-center gap-1 px-2 py-1 text-left text-[color:var(--app-text)] hover:bg-[rgba(29,37,36,0.08)]"
    >
      <span aria-hidden="true">{selected ? '*' : ''}</span>
      <span>{label}</span>
    </button>
  );
}

export function RouteButton({
  active,
  testId,
  onClick,
  children,
}: {
  active: boolean;
  testId: string;
  onClick(): void;
  children: ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      data-testid={testId}
      aria-current={active ? 'page' : undefined}
      onClick={onClick}
      className={`h-9 rounded-lg border px-1 text-xs font-semibold transition ${
        active
          ? 'border-[color:var(--app-border)] bg-white text-[color:var(--app-text)] shadow-sm'
          : 'border-transparent bg-transparent text-[color:var(--app-text-muted)] hover:bg-white/65 hover:text-[color:var(--app-text)]'
      }`}
    >
      {children}
    </button>
  );
}

export function SidebarRowButton({
  active,
  count,
  testId,
  onClick,
  children,
}: {
  active: boolean;
  count: number;
  testId: string;
  onClick(): void;
  children: string;
}): JSX.Element {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={`grid min-h-[22px] w-full grid-cols-[minmax(0,1fr)_max-content] items-center gap-1 rounded-sm py-px pl-2 text-left text-[13px] leading-[18px] text-[color:var(--app-text)] transition ${
        active
          ? 'bg-[color:var(--app-accent-soft)]'
          : 'text-[color:var(--app-text-muted)] hover:bg-white/55'
      }`}
    >
      <span className={`truncate ${active ? 'font-semibold' : ''}`}>{children}</span>
      <span className="justify-self-end rounded-sm bg-[color:var(--app-panel-muted)] px-1 text-right text-[11px] font-semibold leading-4 tabular-nums text-[color:var(--app-text)]">
        {count}
      </span>
    </button>
  );
}
